import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useAccountContext } from '~context/account';
import { useBotPaywallContext } from '~context/botPaywall';
import { usePaywallContext } from '~context/paywall';
import { PaymentReason, PaymentReasonType } from '~features/annals';
import { annals } from '~features/annals/hooks';
import { dialogsApi } from '~services/api/dialogs';
import { eventsInbox, EventType } from '~shared/services/eventsInbox';
import { transformMessagePaymentType } from '~utils/payments';
import { MessagesSwitch } from '~widgets/Chat/ui/MessagesSwitch';

import type { TMessage } from '../../model/types';

export interface RegenerateSwitchProps {
	className?: string;
	userId: string;
	message: TMessage;
	isMessageFromInfluencer: boolean;
}

export const RegenerateSwitch: FC<RegenerateSwitchProps> = ({
	className,
	userId,
	message,
	isMessageFromInfluencer,
}) => {
	const cacheKey = {
		fixedCacheKey: `${message.id}-${message.sender}`,
	};
	const [send, selectionState] =
		dialogsApi.useSelectRegeneratedMessageMutation(cacheKey);
	const [, regenerationState] =
		dialogsApi.useRegenerateMessageInplaceMutation(cacheKey);
	const isLoading = regenerationState.isLoading || selectionState.isLoading;
	const isError = regenerationState.isError || selectionState.isError;
	const [loading, setLoading] = useState(false);
	const { openPaywall } = usePaywallContext();
	const { balance, hasSubscription } = useAccountContext();
	const { openBotPaywall } = useBotPaywallContext();
	const sendAnnalsClicked = annals.chat.useRegenerationClicked();

	const { id: messageId, sender: recipientId, text } = message;
	const { index = 0, total = 0 } = message.meta?.regeneration?.variants || {};
	const { cost = NaN, type } = message.meta?.tariffication?.regeneration || {};
	const regenerationType = transformMessagePaymentType({
		type,
		cost,
		isPaid: false,
		isOwnMessage: false,
	});

	const subscribeAndBuy = useCallback(
		(index: number, isSuccess?: boolean) => {
			if (!isSuccess || loading || isLoading) {
				return;
			}

			const unsub = eventsInbox.onTypeMsg(
				EventType.BalanceChanged,
				(payload) => {
					// ignore decrease balance events
					if (payload.change <= 0) {
						return;
					}

					unsub();

					if (payload.balance < cost) {
						return;
					}

					// TODO: process send error?
					setLoading(true);
					void send({ messageId, userId, recipientId, index });
				}
			);
		},
		[isLoading, loading, messageId, recipientId, cost, send, userId]
	);

	const onChange = (index: number) => {
		void sendAnnalsClicked();

		const regenerate = () => {
			setLoading(true);
			void send({ messageId, userId, recipientId, index });
		};
		const boundSubscribeAndBuy = (isSuccess?: boolean) => {
			subscribeAndBuy(index, isSuccess);
		};

		if (regenerationType.includes('subscription') && !hasSubscription) {
			if (isMessageFromInfluencer) {
				return openBotPaywall({
					reason: PaymentReason.MsgRegeneration,
					reasonType: PaymentReasonType.Chat,
					callback: boundSubscribeAndBuy,
				});
			} else {
				return openPaywall({
					prefer: 'subscription',
					reason: PaymentReason.MsgRegeneration,
					reasonType: PaymentReasonType.Chat,
					botClientId: recipientId,
					callback: boundSubscribeAndBuy,
				})();
			}
		}
		if (regenerationType.includes('subscription-bot') && !hasSubscription) {
			return openBotPaywall({
				reason: PaymentReason.MsgRegeneration,
				reasonType: PaymentReasonType.Chat,
				callback: boundSubscribeAndBuy,
			});
		}

		if (regenerationType.includes('purchase') && balance < cost) {
			return openPaywall({
				reason: PaymentReason.MsgRegeneration,
				reasonType: PaymentReasonType.Chat,
				botClientId: recipientId,
				callback: boundSubscribeAndBuy,
			})();
		}

		return regenerate();
	};

	useEffect(() => {
		setLoading(false);
	}, [messageId, isError, text]);

	return (
		<MessagesSwitch
			className={className}
			onChange={onChange}
			disabled={loading || isLoading}
			value={index}
			total={total}
		/>
	);
};
